<template>
  <AdminLayout>
    <v-container fluid class="pa-6">
      <v-card elevation="3" class="rounded-lg">
        <v-card-title class="d-flex align-center justify-space-between pa-6 bg-gradient-primary">
          <div class="d-flex align-center">
            <v-icon icon="mdi-gift-outline" class="mr-3" size="32" color="white" />
            <span class="text-h5 font-weight-bold text-white">Redemptions Management</span>
          </div>
          <v-btn
            color="white"
            variant="elevated"
            prepend-icon="mdi-refresh"
            size="large"
            class="px-6"
            @click="fetchRedemptions"
            :loading="loading"
          >
            Refresh
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div v-if="loading" class="d-flex justify-center align-center py-8">
            <v-progress-circular 
              indeterminate 
              color="primary" 
              size="64"
              width="6"
            />
            <span class="ml-4 text-h6 text-medium-emphasis">Loading redemptions...</span>
          </div>
          
          <div v-else>
            <v-alert
              v-if="showSuccess"
              type="success"
              class="mb-6"
              closable
              variant="tonal"
              border="start"
              border-color="success"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-check-circle" color="success" />
              </template>
              <div class="d-flex align-center">
                <span class="font-weight-medium">{{ successMessage }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  @click="showSuccess = false"
                ></v-btn>
              </div>
            </v-alert>
            
            <v-alert
              v-if="showError"
              type="error"
              class="mb-6"
              closable
              variant="tonal"
              border="start"
              border-color="error"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-alert-circle" color="error" />
              </template>
              <div class="d-flex align-center">
                <span class="font-weight-medium">{{ errorMessage }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  @click="showError = false"
                ></v-btn>
              </div>
            </v-alert>

            <div v-if="redemptions.length > 0">
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div class="d-flex align-center">
                      <v-icon icon="mdi-information" color="info" class="mr-2" />
                      <span class="text-body-1 text-medium-emphasis">
                        {{ filteredRedemptions.length }} of {{ redemptions.length }} redemption{{ redemptions.length !== 1 ? 's' : '' }} found
                      </span>
                    </div>
                    
                    <div class="d-flex align-center">
                      <v-select
                        v-model="statusFilter"
                        :items="filterOptions"
                        label="Filter by status"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="filter-dropdown"
                        style="min-width: 200px;"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
              
              <v-list class="redemptions-list">
                <v-list-item
                  v-for="redemption in filteredRedemptions" 
                  :key="redemption.id"
                  class="redemption-item"
                  :class="{ 'claimed': redemption.claimed }"
                >
                  <template v-slot:prepend>
                    <v-avatar 
                      :color="redemption.claimed ? 'success' : 'warning'" 
                      size="48"
                      class="mr-4"
                    >
                      <v-icon 
                        :icon="redemption.claimed ? 'mdi-check-circle' : 'mdi-clock'" 
                        size="24"
                        color="white"
                      />
                    </v-avatar>
                  </template>

                  <div class="redemption-content">
                    <v-list-item-title class="text-h6 font-weight-bold mb-2">
                      {{ redemption.itemName }}
                    </v-list-item-title>
                    
                    <v-list-item-subtitle class="mb-0">
                      <div class="d-flex align-center mb-1">
                        <v-icon icon="mdi-account" color="primary" class="mr-2" size="16" />
                        <span class="text-body-2 font-weight-medium">User:</span>
                        <span class="text-body-2 text-medium-emphasis ml-2 font-weight-medium">
                          @{{ redemption.userHandle || 'Unknown User' }}
                        </span>
                      </div>
                      
                      <div class="d-flex align-center mb-1">
                        <v-icon icon="mdi-calendar" color="primary" class="mr-2" size="16" />
                        <span class="text-body-2 font-weight-medium">Redeemed:</span>
                        <span class="text-body-2 text-medium-emphasis ml-2">
                          {{ formatDate(redemption.timestamp) }}
                        </span>
                      </div>
                      
                      <div v-if="redemption.claimed" class="d-flex align-center">
                        <v-icon icon="mdi-check" color="success" class="mr-2" size="16" />
                        <span class="text-body-2 font-weight-medium">Claimed:</span>
                        <span class="text-body-2 text-medium-emphasis ml-2">
                          {{ formatDate(redemption.claimedAt || 0) }}
                        </span>
                      </div>
                      
                      <!-- Spacer for consistent height when not claimed -->
                      <div v-if="!redemption.claimed" class="d-flex align-center" style="height: 20px;">
                        <span class="text-body-2 text-transparent">Claimed:</span>
                      </div>
                    </v-list-item-subtitle>
                  </div>

                  <template v-slot:append>
                    <div class="redemption-actions">
                      <!-- Points Display -->
                      <v-chip
                        color="primary"
                        variant="flat"
                        size="small"
                        class="mr-3"
                      >
                        <v-icon icon="mdi-currency-usd" class="mr-1" />
                        {{ redemption.points }} Tokens
                      </v-chip>
                      
                      <!-- Status Chip -->
                      <v-chip
                        :color="redemption.claimed ? 'success' : 'warning'"
                        variant="flat"
                        size="small"
                        class="mr-3"
                      >
                        <v-icon 
                          :icon="redemption.claimed ? 'mdi-check-circle' : 'mdi-clock'" 
                          class="mr-1" 
                        />
                        {{ redemption.claimed ? 'Completed' : 'Awaiting Claim' }}
                      </v-chip>
                      
                      <!-- Action Button -->
                      <v-btn
                        v-if="!redemption.claimed"
                        color="success"
                        variant="elevated"
                        size="default"
                        prepend-icon="mdi-check"
                        @click="markAsClaimed(redemption)"
                        :loading="redemption.updating"
                        class="mr-2"
                      >
                        Mark as Claimed
                      </v-btn>
                      
                      <v-btn
                        v-else
                        color="info"
                        variant="outlined"
                        size="default"
                        prepend-icon="mdi-eye"
                        @click="viewDetails(redemption)"
                      >
                        View Details
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </div>
            
            <div v-else class="text-center pa-12">
              <v-icon icon="mdi-gift-off" size="80" color="grey-lighten-1" class="mb-4" />
              <h3 class="text-h5 text-grey-darken-1 mb-2">No Redemptions Found</h3>
              <p class="text-body-1 text-medium-emphasis mb-6">
                When users redeem rewards, they will appear here for you to manage
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './components/AdminLayout.vue'
import { useAuthStore } from './stores/authStore'

interface Redemption {
  id: string
  userId: string
  itemId: string
  itemName: string
  points: number
  timestamp: number
  workspaceId: string
  claimed?: boolean
  claimedAt?: number
  updating?: boolean
  userHandle: string
}

const router = useRouter()
const loading = ref(false)
const redemptions = ref<Redemption[]>([])
const showSuccess = ref(false)
const showError = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const statusFilter = ref('all')

const filterOptions = [
  { title: 'All Redemptions', value: 'all' },
  { title: 'Awaiting Claim', value: 'pending' },
  { title: 'Claimed', value: 'claimed' }
]

const filteredRedemptions = computed(() => {
  if (statusFilter.value === 'all') {
    return redemptions.value
  } else if (statusFilter.value === 'pending') {
    return redemptions.value.filter(r => !r.claimed)
  } else if (statusFilter.value === 'claimed') {
    return redemptions.value.filter(r => r.claimed)
  }
  return redemptions.value
})

const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const fetchRedemptions = async () => {
  loading.value = true
  showError.value = false
  
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/admin/redemptions`, {
      credentials: 'include'
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch redemptions')
    }
    
    const data = await response.json()
    console.log('Fetched redemptions data:', data)
    
    redemptions.value = data.map((redemption: any) => {
      console.log('Processing redemption:', redemption)
      return {
        ...redemption,
        updating: false
      }
    })
    
    console.log('Processed redemptions:', redemptions.value)
  } catch (error) {
    console.error('Error fetching redemptions:', error)
    errorMessage.value = 'Failed to load redemptions'
    showError.value = true
  } finally {
    loading.value = false
  }
}

const markAsClaimed = async (redemption: Redemption) => {
  redemption.updating = true
  
  try {
    console.log('Marking redemption as claimed:', redemption.id)
    
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/admin/redemptions/${redemption.id}/claim`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        claimed: true,
        claimedAt: Date.now()
      })
    })
    
    console.log('Response status:', response.status)
    console.log('Response ok:', response.ok)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Error response data:', errorData)
      throw new Error(`Failed to mark redemption as claimed: ${response.status} ${response.statusText}`)
    }
    
    // Update local state
    redemption.claimed = true
    redemption.claimedAt = Date.now()
    
    successMessage.value = `Redemption "${redemption.itemName}" for @${redemption.userHandle} marked as claimed`
    showSuccess.value = true
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      showSuccess.value = false
    }, 3000)
    
  } catch (error: any) {
    console.error('Error marking redemption as claimed:', error)
    errorMessage.value = error.message || 'Failed to mark redemption as claimed'
    showError.value = true
  } finally {
    redemption.updating = false
  }
}

const viewDetails = (redemption: Redemption) => {
  // For now, just show an alert with details
  // In the future, this could open a detailed modal
  alert(`Redemption Details:\n\nItem: ${redemption.itemName}\nUser: @${redemption.userHandle || 'Unknown User'}\nPoints: ${redemption.points}\nRedeemed: ${formatDate(redemption.timestamp)}\nClaimed: ${formatDate(redemption.claimedAt || 0)}`)
}

const { checkAuth } = useAuthStore()

onMounted(async () => {
  try {
    // Check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    await fetchRedemptions()
  } catch (error) {
    console.error('Error loading admin redemptions:', error)
  }
})
</script>

<style scoped>
.redemptions-list {
  background: transparent;
}

.redemption-item {
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.redemption-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.redemption-item.claimed {
  opacity: 0.8;
  background-color: #f8f9fa;
}

.redemption-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 80px;
  padding-top: 8px;
}

.redemption-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  min-height: 80px;
  padding-top: 8px;
  flex-shrink: 0;
}

.font-family-mono {
  font-family: 'Courier New', monospace;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6) !important;
}

.text-white {
  color: white !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-h5 {
  font-size: 1.5rem !important;
  line-height: 2rem !important;
}

.text-h6 {
  font-size: 1.25rem !important;
  line-height: 1.75rem !important;
}

.text-body-1 {
  font-size: 1rem !important;
  line-height: 1.5rem !important;
}

.text-body-2 {
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
}

.rounded-lg {
  border-radius: 12px;
}

.pa-6 {
  padding: 24px;
}

.mb-1 {
  margin-bottom: 4px;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-6 {
  margin-bottom: 24px;
}

.ml-2 {
  margin-left: 8px;
}

.ml-4 {
  margin-left: 16px;
}

.mr-1 {
  margin-right: 4px;
}

.mr-2 {
  margin-right: 8px;
}

.mr-3 {
  margin-right: 12px;
}

.mr-4 {
  margin-right: 16px;
}

.px-6 {
  padding-left: 24px;
  padding-right: 24px;
}

.py-8 {
  padding-top: 32px;
  padding-bottom: 32px;
}

.pa-4 {
  padding: 16px;
}

.pt-0 {
  padding-top: 0;
}

.ma-2 {
  margin: 8px;
}

.h-100 {
  height: 100%;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.justify-space-between {
  justify-content: space-between;
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}

.top-0 {
  top: 0;
}

.right-0 {
  right: 0;
}

.left-0 {
  left: 0;
}
</style> 